import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

/**
 * PRD calendar-ux-parity (REQ-CUX-001): the calendar's view mode and active
 * filters are URL-synced via `?view=`, `?date=`, `?customer=`, `?service=`,
 * `?resource=`, `?state=`. Browser back/forward restores prior selections.
 *
 * Multi-value params (services, resources, states) are encoded as
 * comma-separated values, matching the deferred server-side filter contract
 * in `_conventions.md` and the PRD § "Optional server-side filtering".
 *
 * `view=invalid` resets silently to `day` (PRD §"Error Handling").
 */

export type CalendarView = "day" | "week" | "month" | "resource";

export const VALID_VIEWS: readonly CalendarView[] = ["day", "week", "month", "resource"];

export interface CalendarUrlState {
  view: CalendarView;
  /** Calendar focus date as YYYY-MM-DD; undefined → today. */
  date?: string;
  /** Selected resource id for week/resource view; undefined → first resource. */
  resourceId?: string;
  /** Filter values (multi where applicable). */
  customer?: string;
  services: string[];
  resources: string[];
  states: string[];
  search: string;
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function parseView(raw: string | null): CalendarView {
  if (raw && (VALID_VIEWS as readonly string[]).includes(raw)) return raw as CalendarView;
  return "day";
}

export function useCalendarUrl(): {
  state: CalendarUrlState;
  setView: (view: CalendarView, opts?: { date?: string }) => void;
  setDate: (date: string | undefined) => void;
  setResourceId: (id: string | undefined) => void;
  setCustomer: (id: string | undefined) => void;
  setServices: (ids: string[]) => void;
  setResourcesFilter: (ids: string[]) => void;
  setStates: (states: string[]) => void;
  setSearch: (q: string) => void;
  clearFilters: () => void;
} {
  const [params, setParams] = useSearchParams();

  const state: CalendarUrlState = useMemo(
    () => ({
      view: parseView(params.get("view")),
      date: params.get("date") ?? undefined,
      resourceId: params.get("resource_id") ?? undefined,
      customer: params.get("customer") ?? undefined,
      services: parseList(params.get("service")),
      resources: parseList(params.get("resource")),
      states: parseList(params.get("state")),
      search: params.get("q") ?? ""
    }),
    [params]
  );

  // Mutation helpers compose by reading the current params. We avoid
  // `params` in the dep list so the returned setters stay referentially
  // stable across renders — important for React's effect-deps semantics.
  const update = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutator(next);
          return next;
        },
        { replace: false }
      );
    },
    [setParams]
  );

  const setView = useCallback(
    (view: CalendarView, opts?: { date?: string }) => {
      update((next) => {
        if (view === "day") {
          next.delete("view");
        } else {
          next.set("view", view);
        }
        if (opts?.date) next.set("date", opts.date);
      });
    },
    [update]
  );

  const setDate = useCallback(
    (date: string | undefined) => {
      update((next) => {
        if (date) next.set("date", date);
        else next.delete("date");
      });
    },
    [update]
  );

  const setResourceId = useCallback(
    (id: string | undefined) => {
      update((next) => {
        if (id) next.set("resource_id", id);
        else next.delete("resource_id");
      });
    },
    [update]
  );

  const setCustomer = useCallback(
    (id: string | undefined) => {
      update((next) => {
        if (id) next.set("customer", id);
        else next.delete("customer");
      });
    },
    [update]
  );

  const setServices = useCallback(
    (ids: string[]) => {
      update((next) => {
        if (ids.length === 0) next.delete("service");
        else next.set("service", ids.join(","));
      });
    },
    [update]
  );

  const setResourcesFilter = useCallback(
    (ids: string[]) => {
      update((next) => {
        if (ids.length === 0) next.delete("resource");
        else next.set("resource", ids.join(","));
      });
    },
    [update]
  );

  const setStates = useCallback(
    (states: string[]) => {
      update((next) => {
        if (states.length === 0) next.delete("state");
        else next.set("state", states.join(","));
      });
    },
    [update]
  );

  const setSearch = useCallback(
    (q: string) => {
      update((next) => {
        if (q) next.set("q", q);
        else next.delete("q");
      });
    },
    [update]
  );

  /**
   * Bulk reset of all filter params (customer, services, resources, states,
   * search). Combining these into a single mutator avoids the React-batching
   * race that would otherwise happen if each setter were called separately
   * inside a `useSearchParams` consumer — every call clones the prior
   * URLSearchParams, so successive calls in the same handler can stomp each
   * other when React schedules the updates.
   */
  const clearFilters = useCallback(() => {
    update((next) => {
      next.delete("customer");
      next.delete("service");
      next.delete("resource");
      next.delete("state");
      next.delete("q");
    });
  }, [update]);

  return {
    state,
    setView,
    setDate,
    setResourceId,
    setCustomer,
    setServices,
    setResourcesFilter,
    setStates,
    setSearch,
    clearFilters
  };
}
